import { ListItem } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EmployeeAvatar from 'components/@home/EmployeeAvatar';
import LineClampTooltip from 'components/common/LineClampTooltip';
import ArticleIcon from '@mui/icons-material/Article';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import moment from 'moment';
import { bool, object, oneOf, string } from 'prop-types';
import React, { memo } from 'react';
import ReactTimeAgo from 'react-time-ago';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';

const StatusBadge = ({ Icon, label, variant }) => {
  const color =
    variant === 'active'
      ? 'success.main'
      : variant === 'primary'
        ? 'primary.main'
        : 'text.disabled';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        color,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 500,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Icon sx={{ fontSize: 14 }} />
    </Box>
  );
};

const MemberListItem = ({
  member,
  showReadStatus,
  contentMode = null,
  engagementGroup = null,
  style = {},
  ...rowAria
}) => {
  const locale = useSelector(state => state.i18n.locale);
  let status = member.isAcceptTerms ? 'active' : 'pending';
  if (status === 'active' && member.isAdmin) {
    status = 'admin';
  }

  const isDimmed = engagementGroup === 'none';

  // Derive seen from engagementGroup so we're not dependent on member.read during backend transition
  const hasSeen = member.read || engagementGroup === 'full' || engagementGroup === 'partial';
  const SeenIcon = hasSeen ? CheckCircleIcon : CheckCircleOutlineIcon;
  const seenVariant = hasSeen ? 'primary' : 'inactive';
  let seenLabel = I18n.t('EmployeeListDrawer.pending');

  if (hasSeen) {
    seenLabel = member.readAt ? (
      <LineClampTooltip lines={1} title={moment(member.readAt).locale(locale).format('LLLL')}>
        <span>
          <ReactTimeAgo date={new Date(member.readAt)} locale={locale} tooltip={false} />
        </span>
      </LineClampTooltip>
    ) : (
      I18n.t('EmployeeListDrawer.seen')
    );
  }

  let contentVariant = 'inactive';
  let contentLabel = I18n.t('EmployeeListDrawer.pending');
  let ContentIcon;

  if (contentMode === 'video') {
    contentVariant = member.videoWatched ? 'active' : 'inactive';
    contentLabel = member.videoPercentWatched
      ? I18n.t('EmployeeListDrawer.watched', { percentage: member.videoPercentWatched })
      : I18n.t('EmployeeListDrawer.pending');
    ContentIcon = member.videoWatched ? PlayCircleIcon : PlayCircleOutlineIcon;
  } else if (contentMode === 'article') {
    contentVariant = member.articleRead ? 'active' : 'inactive';
    contentLabel = member.articleRead
      ? I18n.t('EmployeeListDrawer.read')
      : I18n.t('EmployeeListDrawer.pending');
    ContentIcon = member.articleRead ? ArticleIcon : ArticleOutlinedIcon;
  }

  const jobLine = [member.jobTitle, member.department].filter(Boolean).join(', ');

  return (
    <ListItem
      disableGutters
      sx={{
        boxSizing: 'border-box',
        alignItems: 'stretch',
        opacity: isDimmed ? 0.5 : 1,
      }}
      style={style}
      {...rowAria}
    >
      <Box sx={{ mr: 1.5, flexShrink: 0 }}>
        <EmployeeAvatar large employee={member} status={status} />
      </Box>
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flexShrink: 0, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                flex: 1,
                minWidth: 0,
                color: !member.isAcceptTerms ? 'secondary.main' : 'inherit',
              }}
            >
              {member.name}
            </Typography>
          </Box>
          {jobLine ? (
            <Typography
              component="div"
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
                mt: 0.25,
                mb: 0.75,
                lineHeight: 1.43,
              }}
            >
              {jobLine}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {showReadStatus && (
        <Stack
          direction="column"
          sx={{
            gap: 1,
            borderBottom: 1,
            borderColor: 'divider',
            alignItems: 'flex-end',
          }}
        >
          <StatusBadge Icon={SeenIcon} label={seenLabel} variant={seenVariant} />
          {!ContentIcon || (
            <StatusBadge Icon={ContentIcon} label={contentLabel} variant={contentVariant} />
          )}
        </Stack>
      )}
    </ListItem>
  );
};

MemberListItem.propTypes = {
  member: object.isRequired,
  showReadStatus: bool.isRequired,
  contentMode: string,
  engagementGroup: oneOf(['full', 'partial', 'none']),
  style: object,
};

export default memo(MemberListItem);
